import {useAuth0} from '@auth0/auth0-react';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {useLoginContextGetter} from './Auth0Provider';
import {useOptionalTheme} from '../../theme/components/ThemeProvider';
import {getCurrentPath, getInviteRedirectParams, stripInviteParams} from '../utils/location.utils';
import {getLoginAuthorizationParams} from '../utils/login-params.utils';
import {clearRememberedOrganization} from '../utils/remembered-organization.utils';
import {isLikelySignedIn} from '../utils/session-guess.utils';

import type {InviteRedirectParams} from '../utils/location.utils';
import type {FC, ReactNode} from 'react';

export interface Auth0GateProps {
  loadingContent: ReactNode;
  /** Shown instead of `loadingContent` while Auth0 confirms a likely sign-in, e.g. an app outline. */
  returningLoadingContent?: ReactNode;
  errorContent: (errorCode: string, onRetry: () => void, description?: string) => ReactNode;
  children: ReactNode;
  uiLocales?: string;
}

export const Auth0Gate: FC<Auth0GateProps> = ({
  loadingContent,
  returningLoadingContent = loadingContent,
  errorContent,
  children,
  uiLocales
}) => {
  const {isLoading, isAuthenticated, loginWithRedirect, error} = useAuth0();
  const {i18n} = useTranslation();
  const locale = uiLocales ?? i18n.language;
  const theme = useOptionalTheme()?.theme;
  const getLoginContext = useLoginContextGetter();

  const inviteParams = useMemo(getInviteRedirectParams, []);
  const expectsSignedIn = useMemo(isLikelySignedIn, []);

  const redirectToLogin = useCallback(
    (extraParams: Partial<InviteRedirectParams> = {}) =>
      loginWithRedirect({
        openUrl: url => window.location.replace(url),
        authorizationParams: {
          ...getLoginAuthorizationParams({
            locale,
            theme,
            context: getLoginContext()
          }),
          ...extraParams
        },
        appState: {returnTo: stripInviteParams(getCurrentPath())}
      }),
    [loginWithRedirect, locale, theme, getLoginContext]
  );

  useEffect(() => {
    if (isLoading || error) return;

    if (inviteParams) {
      void redirectToLogin(inviteParams);
      return;
    }

    if (!isAuthenticated) {
      void redirectToLogin();
    }
  }, [isLoading, isAuthenticated, error, inviteParams, redirectToLogin]);

  if (error) {
    const {error: errorCode = 'unknown', error_description: description} = error as {
      error?: string;
      error_description?: string;
    };
    return (
      <>
        {errorContent(
          errorCode,
          () => {
            if (getLoginContext().kind === 'member') clearRememberedOrganization();
            void redirectToLogin({...(inviteParams ?? {}), prompt: 'login'});
          },
          description
        )}
      </>
    );
  }

  if (isLoading || !isAuthenticated || inviteParams) {
    // Once Auth0 answers "signed out", fall back to the login look for the redirect.
    const isConfirmingSignIn = isLoading && expectsSignedIn && !inviteParams;
    return <>{isConfirmingSignIn ? returningLoadingContent : loadingContent}</>;
  }

  return <>{children}</>;
};
